import { confirmAction } from '@/utils/alerts'
import eventBus from '@/utils/eventBus'

export const CREDIT_WALLET_EMPTY_CODE = 'credit_wallet_empty'
export const CREDIT_WALLET_PATH = '/manage/subscription'
export const CREDIT_WALLET_QUERY = { tab: 'wallet' }

const STORAGE_KEY = 'clinch.creditWalletAi'
const ALERT_SHOWN_KEY = 'clinch.creditWalletEmptyAlerted'

export const CREDIT_WALLET_EMPTY_HTML = `
  <p>Your plan’s included AI chats for this month are used up. Extra AI is billed from the <strong>Credit wallet</strong> under Usage &amp; billing.</p>
  <p>The Credit wallet balance is <strong>R0.00</strong>, so AI is paused:</p>
  <ul style="text-align:left;margin:0.75rem auto;max-width:24rem;padding-left:1.25rem">
    <li>WhatsApp shopping assistant</li>
    <li>Structure with AI (products)</li>
    <li>Write with AI (templates)</li>
    <li>Agent builder (Build with AI)</li>
    <li>Agent testing (Test with AI and evaluation runs)</li>
  </ul>
  <p>Top up the Credit wallet to keep using AI after the plan include.</p>
`

export const CREDIT_WALLET_WHATSAPP_HTML = `
  <p>WhatsApp conversations are billed from the <strong>Credit wallet</strong> under Usage &amp; billing, using the Clinch billing rates.</p>
  <p>The Credit wallet balance is <strong>R0.00</strong>, so outbound WhatsApp is paused:</p>
  <ul style="text-align:left;margin:0.75rem auto;max-width:24rem;padding-left:1.25rem">
    <li>Team Inbox replies and templates</li>
    <li>Broadcasts</li>
    <li>WhatsApp shopping assistant</li>
  </ul>
  <p>Top up the Credit wallet to send WhatsApp conversations again.</p>
`

export const CREDIT_WALLET_BOTH_HTML = `
  <p>The Credit wallet balance is <strong>R0.00</strong>. WhatsApp conversations and extra AI are billed from this wallet using the Clinch billing rates.</p>
  <p>Outbound WhatsApp is paused:</p>
  <ul style="text-align:left;margin:0.75rem auto;max-width:24rem;padding-left:1.25rem">
    <li>Team Inbox replies and templates</li>
    <li>Broadcasts</li>
    <li>WhatsApp shopping assistant</li>
  </ul>
  <p>Plan-included AI chats are also used up, so extra AI is paused (Structure with AI, Write with AI, Agent builder/testing).</p>
  <p>Top up the Credit wallet to continue.</p>
`

let state = {
  balance: null,
  billedAiBlocked: null,
  billedWhatsappBlocked: null,
  allowances: { free_conversations: [], ai: {} },
}

export function creditWalletSnapshot() {
  return { ...state }
}

export function isBilledAiLocked() {
  return state.billedAiBlocked === true
}

export function isBilledWhatsappLocked() {
  return state.billedWhatsappBlocked === true
}

export function isCreditWalletLocked() {
  return isBilledAiLocked() || isBilledWhatsappLocked()
}

export function creditWalletAllowances() {
  return state.allowances || { free_conversations: [], ai: {} }
}

function normalizeAllowances(data) {
  if (data?.allowances && typeof data.allowances === 'object') {
    return {
      free_conversations: Array.isArray(data.allowances.free_conversations)
        ? data.allowances.free_conversations
        : [],
      ai: data.allowances.ai || {},
    }
  }
  const included = Number(data?.ai_included)
  if (!Number.isFinite(included)) {
    return state.allowances || { free_conversations: [], ai: {} }
  }
  const used = Number(data?.ai_used) || 0
  const remaining = Number.isFinite(Number(data?.ai_remaining))
    ? Number(data.ai_remaining)
    : Math.max(0, included - used)
  return {
    free_conversations: state.allowances?.free_conversations || [],
    ai: {
      included,
      used,
      remaining,
      overage: Number(data?.ai_overage) || 0,
      charging: remaining <= 0,
      label: 'Plan AI chats',
    },
  }
}

export function applyCreditWalletPayload(data) {
  const balance = data?.balance ?? null
  let billedAiBlocked = null
  if (data?.billed_ai_blocked === true) billedAiBlocked = true
  else if (data?.billed_ai_blocked === false) billedAiBlocked = false
  else if (data?.billed_ai_enabled === false) billedAiBlocked = true
  else if (data?.billed_ai_enabled === true) billedAiBlocked = false

  let billedWhatsappBlocked = null
  if (data?.billed_whatsapp_blocked === true) billedWhatsappBlocked = true
  else if (data?.billed_whatsapp_blocked === false) billedWhatsappBlocked = false
  else if (data?.billed_whatsapp_enabled === false) billedWhatsappBlocked = true
  else if (data?.billed_whatsapp_enabled === true) billedWhatsappBlocked = false

  const allowances = normalizeAllowances(data)
  state = { balance, billedAiBlocked, billedWhatsappBlocked, allowances }
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {
    // private mode / quota
  }
  eventBus.emit('credit-wallet-ai-changed', {
    ...state,
    locked: isBilledAiLocked(),
    whatsappLocked: isBilledWhatsappLocked(),
    creditLocked: isCreditWalletLocked(),
  })
  return state
}

export function hydrateCreditWalletAi() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed = JSON.parse(raw)
    state = {
      balance: parsed.balance ?? null,
      billedAiBlocked: parsed.billedAiBlocked ?? null,
      billedWhatsappBlocked: parsed.billedWhatsappBlocked ?? null,
      allowances: parsed.allowances || { free_conversations: [], ai: {} },
    }
  } catch {
    state = {
      balance: null,
      billedAiBlocked: null,
      billedWhatsappBlocked: null,
      allowances: { free_conversations: [], ai: {} },
    }
  }
}

function emptyAlertCopy() {
  const whatsapp = isBilledWhatsappLocked()
  const ai = isBilledAiLocked()
  if (whatsapp && ai) {
    return { title: 'Credit wallet is empty', html: CREDIT_WALLET_BOTH_HTML }
  }
  if (whatsapp) {
    return { title: 'Credit wallet is empty', html: CREDIT_WALLET_WHATSAPP_HTML }
  }
  return { title: 'Plan AI chats used up', html: CREDIT_WALLET_EMPTY_HTML }
}

export async function showCreditWalletEmptyAlert() {
  const copy = emptyAlertCopy()
  return confirmAction({
    title: copy.title,
    html: copy.html,
    icon: 'warning',
    confirmText: 'Top up wallet',
    cancelText: 'Close',
  })
}

export function shouldAutoShowCreditWalletEmptyAlert() {
  if (!isCreditWalletLocked()) return false
  try {
    if (sessionStorage.getItem(ALERT_SHOWN_KEY) === '1') return false
    sessionStorage.setItem(ALERT_SHOWN_KEY, '1')
    return true
  } catch {
    return true
  }
}

export function clearCreditWalletEmptyAlertFlag() {
  try {
    sessionStorage.removeItem(ALERT_SHOWN_KEY)
  } catch {
    // ignore
  }
}

export function isCreditWalletEmptyError(err) {
  return err?.response?.data?.code === CREDIT_WALLET_EMPTY_CODE
}

export async function promptCreditWalletEmptyIfNeeded(err, router) {
  if (!isCreditWalletEmptyError(err)) return false
  const data = err?.response?.data || {}
  applyCreditWalletPayload({
    billed_ai_blocked: data.billed_ai_blocked === true,
    billed_whatsapp_blocked: data.billed_whatsapp_blocked !== false,
    billed_ai_enabled: data.billed_ai_enabled,
    billed_whatsapp_enabled: data.billed_whatsapp_enabled,
    balance: data.balance ?? '0.00',
  })
  const go = await showCreditWalletEmptyAlert()
  if (go && router) {
    router.push({ path: CREDIT_WALLET_PATH, query: CREDIT_WALLET_QUERY })
  }
  return true
}

hydrateCreditWalletAi()
